import { Star } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { Sentry } from "@/lib/sentry";

export const PHOTO_BUCKET = "collection-photos";

export function isHttp(s: string) {
  return s.startsWith("data:") || s.startsWith("blob:");
}

/**
 * Extract the storage object path from either a raw path or a legacy
 * public/signed URL pointing at our bucket.
 */
export function toObjectPath(src: string): string | null {
  if (!src) return null;
  if (isHttp(src)) return null;
  // Legacy stored full URLs — extract the path after the bucket name.
  const m = src.match(/\/collection-photos\/([^?]+)/);
  if (m) return decodeURIComponent(m[1]);
  if (/^https?:\/\//i.test(src)) return null;
  return src;
}

/**
 * Best-effort deletion of a stored photo object given its DB photo_url value
 * (raw path or legacy full URL). Never throws; failures are reported to
 * Sentry so orphaned files can be cleaned up manually.
 */
export async function deleteStoredPhoto(
  photoUrl: string | null | undefined,
  context?: Record<string, unknown>,
): Promise<void> {
  if (!photoUrl) return;
  try {
    const path = toObjectPath(photoUrl);
    if (!path) return;
    const { error } = await supabase.storage.from(PHOTO_BUCKET).remove([path]);
    if (error) {
      Sentry.captureException(
        new Error(`storage-delete-failed: ${error.message}`),
        { tags: { source: "storage-delete" }, extra: { path, ...context } },
      );
    }
    // Drop cached signed URL — the object is gone.
    signedUrlCache.delete(path);
  } catch (e) {
    Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
      tags: { source: "storage-delete" },
      extra: { photoUrl, ...context },
    });
  }
}

// In-memory cache so we don't re-sign on every render.
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>();
const SIGN_TTL_SECONDS = 3600;
const SIGN_TIMEOUT_MS = 8000;

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("signed-url-timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

export async function signPath(path: string): Promise<string | null> {
  const cached = signedUrlCache.get(path);
  if (cached && cached.expiresAt > Date.now() + 60_000) return cached.url;
  const attempt = async () => {
    const { data, error } = await supabase.storage
      .from(PHOTO_BUCKET)
      .createSignedUrl(path, SIGN_TTL_SECONDS);
    if (error || !data?.signedUrl) return null;
    return data.signedUrl;
  };
  let signed: string | null = null;
  try {
    signed = await withTimeout(attempt(), SIGN_TIMEOUT_MS);
  } catch {
    // Timed out or threw — force one retry with a fresh request.
    signedUrlCache.delete(path);
    try {
      signed = await withTimeout(attempt(), SIGN_TIMEOUT_MS);
    } catch {
      signed = null;
    }
  }
  if (!signed) return null;
  signedUrlCache.set(path, {
    url: signed,
    expiresAt: Date.now() + SIGN_TTL_SECONDS * 1000,
  });
  return signed;
}

export function useResolvedPhotoUrl(src: string | null) {
  const [url, setUrl] = useState<string | null>(
    src && isHttp(src) ? src : null,
  );

  const refresh = useCallback(async () => {
    if (!src) {
      setUrl(null);
      return;
    }
    if (isHttp(src)) {
      setUrl(src);
      return;
    }
    const path = toObjectPath(src);
    if (!path) {
      setUrl(null);
      return;
    }
    Sentry.addBreadcrumb({
      category: "signed-url",
      level: "info",
      message: "signed-url-refresh",
      data: { path },
    });
    // Invalidate cache entry first so we get a fresh URL.
    signedUrlCache.delete(path);
    const signed = await signPath(path);
    setUrl(signed);
  }, [src]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!src) return setUrl(null);
      if (isHttp(src)) return setUrl(src);
      const path = toObjectPath(src);
      if (!path) return setUrl(null);
      const signed = await signPath(path);
      if (active) setUrl(signed);
    })();
    return () => {
      active = false;
    };
  }, [src]);

  return { url, refresh };
}

/**
 * Resolves and warms the browser's own HTTP cache for a photo ahead
 * of when it's actually needed — used to prefetch the neighboring
 * items in a swipe-traversal context, so the signed URL is already
 * cached and the image bytes are already downloaded by the time the
 * user actually swipes to it, rather than starting both cold at
 * swipe time.
 */
export function prefetchPhotoUrl(src: string | null): void {
  if (!src) return;
  (async () => {
    let resolved: string | null = null;
    if (isHttp(src)) {
      resolved = src;
    } else {
      const path = toObjectPath(src);
      if (!path) return;
      resolved = await signPath(path);
    }
    if (!resolved) return;
    const img = new Image();
    img.src = resolved;
  })();
}

export function Stars({ value, className }: { value: number; className?: string }) {
  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            "h-3.5 w-3.5",
            i <= value ? "fill-accent text-accent" : "text-muted-foreground/40"
          )}
        />
      ))}
    </div>
  );
}

export function ImageBox({
  src,
  alt,
  className,
  aspect = "aspect-[1/1.15]",
  style,
}: {
  src: string | null;
  alt: string;
  className?: string;
  aspect?: string;
  style?: React.CSSProperties;
}) {
  const { url: resolved, refresh } = useResolvedPhotoUrl(src);
  const [imgLoaded, setImgLoaded] = useState(false);
  return (
    <div className={cn("bg-secondary overflow-hidden relative", aspect, className)} style={{ borderRadius: 14, ...style }}>
      {resolved ? (
        <>
          {!imgLoaded && (
            <div
              className="absolute inset-0 animate-pulse"
              style={{ backgroundColor: "var(--caileve-soft)" }}
            />
          )}
          <img
            src={resolved}
            alt={alt}
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => refresh()}
            style={{
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 220ms ease-in",
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-[10px] uppercase tracking-[0.25em]">
          No image
        </div>
      )}
    </div>
  );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{children}</p>
  );
}